import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Crown, Users, ChevronRight } from "lucide-react";
import { api } from "../lib/apiClient";

/**
 * v3.99j — HotBreedersStrip
 * Compact horizontal carousel of the most-followed / most-active breeders.
 * Renders small (96px) circular logo thumbnails with follower count below.
 * Used on Home (under the breeder grid section) and Trending (above the
 * Forge Index podium).
 *
 * Pure read; sources `/api/marketplace/hot-breeders`. Soft-fails to null on
 * fetch error so the host page never breaks if the endpoint hiccups.
 */
export default function HotBreedersStrip({ limit = 8, title = "Most-followed breeders this week", subtitle = "Whoever the collectors are watching right now" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/marketplace/hot-breeders", { params: { limit } })
      .then((r) => { if (alive) { setItems(Array.isArray(r.data) ? r.data : []); setLoading(false); } })
      .catch(() => { if (alive) { setItems([]); setLoading(false); } });
    return () => { alive = false; };
  }, [limit]);

  if (!loading && items.length === 0) return null;

  return (
    <section className="mt-12 mb-10" data-testid="hot-breeders-strip">
      <div className="flex items-end justify-between mb-5 flex-wrap gap-2">
        <div>
          <div className="label-eyebrow mb-2 flex items-center gap-2">
            <Flame className="w-3 h-3" style={{ color: "var(--secondary)" }} />
            Hot right now
          </div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tighter" style={{ color: "var(--text)" }}>
            {title}
          </h2>
          <p className="text-xs md:text-sm mt-1" style={{ color: "var(--text-muted)" }}>{subtitle}</p>
        </div>
        <Link to="/breeders" className="text-xs font-mono uppercase tracking-[0.18em] inline-flex items-center gap-1 hover:gap-2 transition-all"
              style={{ color: "var(--gold)" }} data-testid="hot-breeders-all-link">
          See all <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      {/* v3.99d.237 — Tuned mobile scroll: loose snap (proximity, not
          mandatory) so flicks feel natural on iOS; smaller touch-friendly
          items on phones; momentum scrolling and scroll-padding so the
          first item peeks from the edge instead of slamming against it. */}
      <div className="flex gap-3 md:gap-4 overflow-x-auto pb-2 -mx-4 sm:-mx-1 px-4 sm:px-1 snap-x snap-proximity scroll-px-4 sm:scroll-px-1"
           style={{ scrollbarWidth: "thin", WebkitOverflowScrolling: "touch", overscrollBehaviorX: "contain" }}>
        {(loading ? Array.from({ length: 6 }) : items).map((v, i) => (
          v ? (
            <Link
              key={v.id}
              to={`/breeder/${v.store_slug || v.id}`}
              className="group flex-shrink-0 w-[88px] sm:w-[112px] md:w-[124px] snap-start text-center"
              data-testid={`hot-breeder-${v.store_slug || v.id}`}
              title={v.store_name}
            >
              <div className="relative w-[72px] sm:w-[88px] md:w-[100px] h-[72px] sm:h-[88px] md:h-[100px] mx-auto bg-black border border-[var(--border)] group-hover:border-[var(--gold)] transition-colors overflow-hidden flex items-center justify-center"
                   style={{ borderRadius: "50%" }}>
                {v.banner_url && (
                  <img src={v.banner_url} alt="" loading="lazy"
                       className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 transition-opacity" />
                )}
                <div className="absolute inset-0"
                     style={{ background: "radial-gradient(ellipse at center, rgba(10,61,42,0.35), transparent 70%)" }} />
                {v.logo_url && (
                  <img src={v.logo_url} alt={v.store_name} loading="lazy"
                       className="relative max-w-[70%] max-h-[70%] object-contain group-hover:scale-110 transition-transform duration-500" />
                )}
                {v.founding_breeder && (
                  <span className="absolute bottom-0 right-0 inline-flex items-center justify-center w-6 h-6 border-2"
                        title="Founding Breeder"
                        style={{
                          background: "var(--bg)",
                          color: "var(--gold)",
                          borderColor: "var(--gold)",
                          borderRadius: "50%",
                        }}>
                    <Crown className="w-3 h-3" />
                  </span>
                )}
              </div>
              <div className="mt-2 sm:mt-2.5 px-1">
                <div className="font-display text-[11px] sm:text-[13px] leading-tight truncate group-hover:text-[var(--primary-text)] transition-colors"
                     style={{ color: "var(--text)" }}>
                  {v.store_name}
                </div>
                <div className="text-[9px] sm:text-[10px] font-mono mt-0.5 inline-flex items-center gap-1 justify-center"
                     style={{ color: "var(--text-muted)" }}>
                  <Users className="w-2.5 h-2.5" />
                  {v.follower_count.toLocaleString()}
                </div>
              </div>
            </Link>
          ) : (
            <div key={i} className="flex-shrink-0 w-[88px] sm:w-[112px] md:w-[124px]" aria-hidden>
              <div className="w-[72px] sm:w-[88px] md:w-[100px] h-[72px] sm:h-[88px] md:h-[100px] mx-auto bg-[var(--surface-2)] border border-[var(--border)]"
                   style={{ borderRadius: "50%" }} />
              <div className="mt-2.5 h-3 bg-[var(--surface-2)] mx-3" />
              <div className="mt-1 h-2 bg-[var(--surface-2)] mx-6" />
            </div>
          )
        ))}
      </div>
    </section>
  );
}
